import { CabinetWrapper } from '../../shared/cabinet-wrapper';
import UITabs from '../../shared/ui/ui-tabs';
import { FactorRequestsBody } from './ui/factor-requests-body';
import {
  useGetFactorRequestsQuery,
  useGetFactorsBranchesQuery,
} from '../../app/api';
import useFilters from '../../app/hooks/useFilters';
import { useFactoringTypeFilter } from '@/app/hooks/useFactoringTypeFilter';
import dayjs from 'dayjs';
import { PageTitle } from '../../shared/page-title';
import { UITabItemLabel } from '../../shared/ui/ui-table/ui/tab-item-label';
import { DatePicker } from '@mui/x-date-pickers';
import { Stack, TextField } from '@mui/material';

import SearchIcon from '@/app/assets/icons/search-alt.svg?react';

import { useTranslation } from 'react-i18next';
import {
  Currency,
  DealCountsStatus,
  FactorDealStatus,
} from '../../app/api/types';
import { TabItem } from '@/shared/ui/ui-tabs/types';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { useAppSelector } from '@/app/hooks';
import { checkRole } from '@/app/helpers';
import { CURRENCY_OPTIONS } from '@/constants';

export interface FactorRequestsFilters {
  dateFrom: string;
  dateTo: string;
  status: FactorDealStatus | string;
  page: number;
  pageSize: number;
  search: string;
  branchId: number | null;
  currency: Currency;
  factoringType: string;
}

const defaultFilters: FactorRequestsFilters = {
  dateFrom: '',
  dateTo: dayjs().format('DD.MM.YYYY'),
  status: '',
  page: 1,
  pageSize: 10,
  search: '',
  branchId: null,
  currency: 'UZS',
  factoringType: 'all',
};

export const FactorRequestsPage = () => {
  const { t } = useTranslation();
  const { profile } = useAppSelector((state) => state.auth);
  const { isAdmin, isHeadOfficeEmployee } = checkRole(profile?.roles);

  const showBranchFilter = isAdmin || isHeadOfficeEmployee;

  const branchIdFromProfile = profile?.branchId;

  const factoringTypeOptions = [
    {
      label: t('factoring_types.all'),
      value: 'all',
      filterValue: undefined,
    },
    {
      label: t('factoring_types.opened'),
      value: 'opened',
      filterValue: 'opened',
    },
    {
      label: t('factoring_types.closed'),
      value: 'closed',
      filterValue: 'closed',
    },
    {
      label: t('factoring_types.reverse'),
      value: 'reverse',
      filterValue: 'reverse',
    },
    {
      label: t('factoring_types.export'),
      value: 'export',
      filterValue: 'export',
    },
    {
      label: t('factoring_types.import'),
      value: 'import',
      filterValue: 'import',
    },
  ];

  const { filters, updateFilters } = useFilters<FactorRequestsFilters>({
    defaultFilters,
    saveToUrl: true,
  });

  const factoringType = useFactoringTypeFilter(
    filters.factoringType,
    (value) => updateFilters({ factoringType: value })
  );

  const { data, isFetching } = useGetFactorRequestsQuery({
    ...filters,
    branchId: showBranchFilter
      ? filters.branchId || undefined
      : branchIdFromProfile || filters.branchId || undefined,
    currency: filters.currency || undefined,
    factoringType: factoringTypeOptions.find(
      (item) => item.value === factoringType
    )?.filterValue,
  });

  const { data: branches, isLoading: isBranchesLoading } =
    useGetFactorsBranchesQuery();

  // contractRejected active underReview contractCreated declined expired completed
  const statuses: {
    status: DealCountsStatus;
    label: string;
    count?: number;
  }[] = [
    {
      status: '',
      label: t('factor_request_tabs.all'),
      count: Object.values(data?.dealsCount ?? {}).reduce(
        (acc, item) => acc + item,
        0
      ),
    },
    {
      status: 'active',
      label: t('factor_request_tabs.active'),
    },
    // {
    //   status: 'completed',
    //   label: t('factor_request_tabs.successful'),
    // },
    {
      status: 'contractCreated',
      label: t('factor_request_tabs.waiting_for_sign'),
    },
    {
      status: 'underReview',
      label: t('factor_request_tabs.underReview'),
    },
    // {
    //   status: 'expired',
    //   label: t('factor_request_tabs.expired'),
    // },
    {
      status: 'declined',
      label: t('factor_request_tabs.declined'),
      count:
        (data?.dealsCount?.declined || 0) +
        (data?.dealsCount?.contractRejected || 0),
    },
  ];

  const tabIndex =
    statuses.findIndex((status) => status.status === filters.status) || 0;

  return (
    <CabinetWrapper>
      <Stack direction='column' spacing={2.5}>
        <PageTitle title={t('factor_requests')} noBack />

        <Stack direction='row' spacing={3}>
          <Stack direction='row' spacing={1}>
            <DatePicker
              label={t('from') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateFrom, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateFrom, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateFrom: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
            <DatePicker
              label={t('to') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateTo, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateTo, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateTo: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
            <TextField
              value={filters.search}
              onChange={(event) =>
                updateFilters({ search: event.target.value })
              }
              label={t('request_number_or_name')}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
              sx={{
                width: 500,
              }}
            />

            {showBranchFilter && (
              <UIAutocomplete
                value={
                  branches?.data?.find(
                    (item) => item.unique === filters.branchId
                  ) ?? null
                }
                options={branches?.data ?? []}
                TextFieldProps={{
                  label: t('branch'),
                  sx: { width: 300 },
                }}
                loading={isBranchesLoading}
                getOptionLabel={(option) => option.branchName}
                onChange={(_event, value) => {
                  updateFilters({ branchId: value?.unique });
                }}
              />
            )}

            <UIAutocomplete
              value={CURRENCY_OPTIONS.find(
                (item) => item.value === filters.currency
              )}
              options={CURRENCY_OPTIONS}
              TextFieldProps={{
                label: t('currency'),
                sx: { width: 125 },
              }}
              getOptionLabel={(option) => {
                return t(option.label);
              }}
              onChange={(_event, value) => {
                updateFilters({ currency: value?.value as Currency });
              }}
              disableClearable
            />
          </Stack>
          <UIAutocomplete
            value={factoringTypeOptions.find(
              (item) => item.value === factoringType
            )}
            options={factoringTypeOptions}
            TextFieldProps={{
              label: t('factoring_type'),
              sx: { width: 300 },
            }}
            getOptionLabel={(option) => option.label}
            onChange={(_e, value) =>
              updateFilters({ factoringType: value?.value })
            }
            disableClearable
          />
        </Stack>

        <UITabs
          value={tabIndex}
          onChange={(_, index) => {
            updateFilters({ status: statuses[index].status, page: 1 });
          }}
          tabs={[
            ...statuses.map(
              (status) =>
                ({
                  label: (
                    <UITabItemLabel
                      label={status.label}
                      count={
                        status?.count || data?.dealsCount?.[status.status] || 0
                      }
                    />
                  ),
                  component: (
                    <FactorRequestsBody
                      data={data?.data}
                      filters={filters}
                      updateFilters={updateFilters}
                      isLoading={isFetching}
                      totalPages={data?.totalPages ?? 0}
                    />
                  ),
                }) as TabItem
            ),
          ]}
        />
      </Stack>
    </CabinetWrapper>
  );
};
